// ============================================================================
// Graph to Code Sync Handler
// ============================================================================

import type { Flow, FunctionStep } from '../../types';
import type { GraphChange, GraphToCodeSyncResult } from '../types';
import { codeInserter } from './inserter';
import { codeModifier } from './modifier';

/**
 * Syncs code from graph changes
 * Updates code when graph structure changes
 * @param {Flow} flow - The flow containing all steps
 * @param {FunctionStep} step - The function step to sync code for
 * @param {string} oldCode - The existing Python code
 * @param {GraphChange} change - The graph change that triggered the sync
 * @param {object} [changeOrigin] - Optional origin of the change to prevent circular syncs
 * @param {string} [changeOrigin.source] - The source of the change (code, graph, system)
 * @return {GraphToCodeSyncResult} result Result containing updated code and any errors
 */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
export function syncCodeFromGraph(
  flow: Flow,
  step: FunctionStep,
  oldCode: string,
  change: GraphChange,
  changeOrigin?: { source: 'code' | 'graph' | 'system' },
): GraphToCodeSyncResult {
  // Prevent circular sync if this was triggered by a code change
  if (changeOrigin?.source === 'code') {
    return { code: oldCode };
  }

  try {
    let newCode = oldCode;

    switch (change.type) {
      case 'condition_added':
        if (change.condition) {
          newCode = codeInserter.insertConditionBlock(
            oldCode,
            change.condition,
            flow,
          );
        }
        break;

      case 'condition_deleted': {
        // Use condition from change if provided, otherwise look it up by conditionId
        const condition =
          change.condition ||
          (change.conditionId
            ? step.conditions.find(c => c.id === change.conditionId)
            : undefined);
        if (condition) {
          newCode = codeModifier.commentOutConditionBlock(
            oldCode,
            condition,
            flow,
          );
        }
        break;
      }

      case 'condition_label_updated':
        if (change.condition && change.oldLabel && change.newLabel) {
          newCode = codeModifier.updateConditionLabel(
            oldCode,
            change.condition,
            change.oldLabel,
            change.newLabel,
            flow,
          );
        }
        break;

      case 'condition_connection_changed':
        if (change.condition && change.oldStepName && change.newStepName) {
          newCode = codeModifier.updateConditionConnection(
            oldCode,
            change.oldStepName,
            change.newStepName,
          );
        }
        break;

      case 'step_name_updated':
        if (change.oldStepName && change.newStepName) {
          // Update all references to this step name in condition blocks
          newCode = codeModifier.updateStepNameInConditionBlocks(
            oldCode,
            change.oldStepName,
            change.newStepName,
          );
        }
        break;

      case 'step_deleted':
        if (change.deletedStepId) {
          newCode = codeModifier.commentOutConditionsReferencingStep(
            oldCode,
            change.deletedStepId,
            flow,
          );
        }
        break;

      case 'exit_flow_condition_added':
        if (change.condition) {
          newCode = codeInserter.insertConditionBlock(
            oldCode,
            change.condition,
            flow,
          );
        }
        break;

      case 'exit_flow_condition_deleted': {
        // Use condition from change if provided, otherwise look it up by conditionId
        const condition =
          change.condition ||
          (change.conditionId
            ? step.conditions.find(c => c.id === change.conditionId)
            : undefined);
        if (condition) {
          newCode = codeModifier.commentOutConditionBlock(
            oldCode,
            condition,
            flow,
          );
        }
        break;
      }

      default:
        return { code: oldCode };
    }

    // Validate the result
    if (!isCodeValid(newCode)) {
      console.warn('Code modification resulted in invalid Python, reverting');
      return {
        code: oldCode,
        errors: [
          {
            type: 'validation_error',
            message: 'Code modification resulted in invalid Python syntax',
          },
        ],
      };
    }

    return { code: newCode };
  } catch (error) {
    console.error('Error syncing code from graph:', error);
    return {
      code: oldCode,
      errors: [
        {
          type: 'sync_error',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      ],
    };
  }
}

/**
 * Validates that code is still valid Python after modification
 * @param {string} code - The Python code to validate
 * @return {boolean} True if the code appears valid, false otherwise
 */
function isCodeValid(code: string): boolean {
  // In browser, we can't validate, so assume it's valid
  if (typeof window !== 'undefined') {
    return true;
  }

  // In server environment, we could validate with AST parser
  // For now, just check basic syntax
  try {
    // Basic validation - check for unmatched brackets, quotes, etc.
    const openBraces = (code.match(/\{/g) || []).length;
    const closeBraces = (code.match(/\}/g) || []).length;
    const openParens = (code.match(/\(/g) || []).length;
    const closeParens = (code.match(/\)/g) || []).length;
    const openBrackets = (code.match(/\[/g) || []).length;
    const closeBrackets = (code.match(/\]/g) || []).length;

    return (
      openBraces === closeBraces &&
      openParens === closeParens &&
      openBrackets === closeBrackets
    );
  } catch {
    return false;
  }
}
